import React, { useState, useRef } from "react";
import "./rotation-plan.css";

const EditableTitle = ({ value, onChange, className, style }) => (
  <input
    className={className}
    style={{
      ...style,
      fontWeight: "bold",
      fontSize: "1.1em",
      border: "none",
      textAlign: "center",
      background: "transparent"
    }}
    value={value}
    onChange={e => onChange(e.target.value)}
    spellCheck={false}
  />
);

const EditableLegend = ({ legend, setLegend }) => (
  <div className="rotation-legend">
    <b>Legend:</b>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "8px" }}>
      {legend.map((item, idx) => (
        <span key={idx} className="legend-item">
          <input
            type="color"
            value={item.color}
            onChange={e =>
              setLegend(l =>
                l.map((it, i) => (i === idx ? { ...it, color: e.target.value } : it))
              )
            }
            style={{ marginRight: 6, verticalAlign: "middle" }}
          />
          <input
            value={item.label}
            onChange={e =>
              setLegend(l =>
                l.map((it, i) => (i === idx ? { ...it, label: e.target.value } : it))
              )
            }
            style={{ width: 100, border: "none", background: "transparent", fontWeight: 600 }}
          />
          <button
            type="button"
            onClick={() => setLegend(l => l.filter((_, i) => i !== idx))}
            style={{ cursor: "pointer" }}
          >
            ❌
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => setLegend(l => [...l, { label: "New", color: "#cccccc" }])}
        style={{ marginLeft: 12, fontWeight: "bold", cursor: "pointer" }}
      >
        + Add
      </button>
    </div>
  </div>
);

const EditableCell = ({ value, color, onChange }) => (
  <td
    style={{
      minWidth: 32,
      height: 34,
      background: color || "#fff",
      border: "1px solid #222",
      cursor: "pointer",
      position: "relative",
      textAlign: "center"
    }}
    onClick={e => {
      e.stopPropagation();
      const newValue = window.prompt("Enter department/subject:", value || "");
      if (newValue !== null && newValue !== value) {
        onChange(newValue);
      }
    }}
    title={value}
  >
    {value}
  </td>
);

export default function RotationPlanEditor() {
  const [college, setCollege] = useState("SAROJINI COLLEGE OF NURSING, DHULE.");
  const [planTitle, setPlanTitle] = useState("MASTER ROTATION PLAN FOR GNM SYLLABUS 3 YEARS COURSE");
  const [yearTitle, setYearTitle] = useState("GNM FIRST YEAR AUGUST-2025 BATCH");
  const [subTitle, setSubTitle] = useState("MASTER ROTATION PLAN FOR YEAR - 2025-2026");

  // Just a regular variable, not state!
  const months = [
    "August", "September", "October", "November", "December", "January",
    "February", "March", "April", "May", "June", "July"
  ];
  const monthColors = [
    "#fce4ec", "#f3e5f5", "#e1f5fe", "#dcedc8", "#ffecb3", "#ffe0b2",
    "#d7ccc8", "#c8e6c9", "#b3e5fc", "#ffccbc", "#cfd8dc", "#fff9c4"
  ];
  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);

  const [legend, setLegend] = useState([
    { label: "Teaching Block", color: "#f44336" },
    { label: "PTS Exam", color: "#2196f3" },
    { label: "Vacation", color: "#fff" },
    { label: "UPH Experience", color: "#cddc39" },
    { label: "RPH Experience", color: "#ffeb3b" },
    { label: "Clinical Experience", color: "#4caf50" },
    { label: "Second Term Exam", color: "#ff9800" },
    { label: "First Term Exam", color: "#9c27b0" },
    { label: "Computer Education", color: "#673ab7" },
    { label: "Preparatory Leave", color: "#ffb74d" },
    { label: "MSBNPE Exam", color: "#ef9a9a" }
  ]);

  const rows = [
    "I Year G.N.M",
    "II Year G.N.M",
    "III Year G.N.M"
  ];

  const [grid, setGrid] = useState(
    rows.map(() =>
      weeks.map(() => ({
        value: "",
        legendIdx: -1
      }))
    )
  );

  const setCellValue = (rowIdx, colIdx, value) => {
    setGrid(g =>
      g.map((row, i) =>
        i !== rowIdx
          ? row
          : row.map((cell, j) =>
              j !== colIdx ? cell : { ...cell, value, legendIdx: -1 }
            )
      )
    );
  };

  const printRef = useRef();
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Rotation Plan</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; font-size: 11px;}
            th, td { border: 1px solid #333; padding: 4px 8px; text-align: center; min-width: 26px;}
            th { background: #e3f2fd; }
            .rotation-legend { margin-top: 22px; font-size: 13px;}
            .legend-item { display: inline-block; margin-right: 22px; }
            .legend-color { display: inline-block; width: 18px; height: 18px; margin-right: 6px; border: 1px solid #333; }
            .signature-row { margin-top: 22px; width: 100%; font-size: 13px; }
            .signature-cell { width: 50%; padding-top: 28px; text-align: center;}
            h2, h3 { text-align: center; margin: 0.3em 0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  return (
    <div className="rotation-plan-wrapper">
      <div ref={printRef}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <EditableTitle value={college} onChange={setCollege} style={{ fontSize: "1.5em" }} /><br />
          <EditableTitle value={planTitle} onChange={setPlanTitle} style={{ fontSize: "1.3em" }} /><br />
          <EditableTitle value={yearTitle} onChange={setYearTitle} style={{ fontSize: "1.1em" }} /><br />
          <EditableTitle value={subTitle} onChange={setSubTitle} style={{ fontSize: "1.1em" }} />
        </div>
        <table className="rotation-plan-table">
          <thead>
            <tr>
              <th rowSpan={3} style={{ minWidth: 70, background: "#fff" }}>Batch/Year</th>
              {months.map((month, idx) => (
                <th
                  key={month}
                  colSpan={4}
                  style={{
                    background: monthColors[idx],
                    color: "#222",
                    fontWeight: 600,
                    fontSize: "1em"
                  }}
                  contentEditable
                  suppressContentEditableWarning
                >
                  {month}
                </th>
              ))}
            </tr>
            <tr>
              {months.map((_, idx) =>
                [0, 1, 2, 3].map(offset => (
                  <th
                    key={idx + "-" + offset}
                    style={{
                      background: monthColors[idx]
                    }}
                  >
                    {weeks[idx * 4 + offset] || ""}
                  </th>
                ))
              )}
            </tr>
            <tr>
              {months.map((_, idx) =>
                [0, 1, 2, 3].map(offset => (
                  <th
                    key={idx + "-date-" + offset}
                    style={{
                      background: monthColors[idx],
                      fontSize: "0.9em"
                    }}
                  >
                    {/* Date cells can be made editable as per your requirement */}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((rowName, rowIdx) => (
              <tr key={rowName}>
                <th style={{ background: "#fafafa" }}>{rowName}</th>
                {weeks.map((_, colIdx) => {
                  const cell = grid[rowIdx][colIdx];
                  return (
                    <EditableCell
                      key={colIdx}
                      value={cell.value}
                      color={cell.legendIdx >= 0 ? legend[cell.legendIdx].color : "#fff"}
                      onChange={val => setCellValue(rowIdx, colIdx, val)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <EditableLegend legend={legend} setLegend={setLegend} />
        <div className="signature-row">
          <div className="signature-cell">
            <b>Sign of Co-ordinator</b>
          </div>
          <div className="signature-cell">
            <b>Sign of Principal</b>
          </div>
        </div>
      </div>
      <button
        className="primary-action"
        style={{
          marginTop: 28,
          background: "#8e24aa",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.15em",
          border: "none",
          padding: "12px 28px",
          borderRadius: 7,
          cursor: "pointer"
        }}
        onClick={handlePrint}
      >
        Download/Print Rotation Plan
      </button>
    </div>
  );
}